import { Dispatch, SetStateAction, useEffect } from 'react';

import { ConditionalRender } from '@crossnokaye/ux-common';

import {
  ActionComponentStateConfig,
  ConfirmationDialogConfig,
  DropdownOption,
  Schedule,
  Sequence,
} from '@repo/sdk/models/components';

import { getRefValue } from '@/features/equipment-browser/utils';
import { keepEscapeInDialog } from '@/utils/keep-escape-in-dialog';
import { InputLabel } from '@/ux-common/components/input/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
} from '@/ux-common/components/select/select';

import { ActionIcon } from './action-icon';

type SettingValueKind = string | number | boolean | Schedule | Sequence | undefined;

export const ConfirmationDescription = ({
  data,
  setData,
  state,
  requiresConfirmation,
}: {
  requiresConfirmation: ConfirmationDialogConfig;
  data: Record<string, SettingValueKind>;
  setData: Dispatch<SetStateAction<Record<string, SettingValueKind>>>;
  state: ActionComponentStateConfig;
}) => {
  const dropdownConfig = requiresConfirmation.dropdownComponentConfig;

  useEffect(() => {
    if (dropdownConfig) {
      const currentRefValue = dropdownConfig?.valueRef
        ? getRefValue(dropdownConfig.valueRef)
        : undefined;
      setData((prev) => ({ ...prev, [dropdownConfig.id as string]: currentRefValue }));
    }
    return () => {
      setData({});
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dropdownConfig]);

  const selectedOption = dropdownConfig?.options.find(
    (option: DropdownOption) => option.value === data[dropdownConfig?.id as string]
  );

  // Each row carries its mode's icon: operators recognise modes by glyph. Design
  // approved icons here as an exception to select.md rule 6 (rows hold text only).
  // Radix copies the chosen row into the field, so the selected mode's icon shows there too.
  return (
    <ConditionalRender condition={!!dropdownConfig}>
      <Select
        disabled={dropdownConfig?.disabled ?? false}
        onValueChange={(value) =>
          setData((prev) => ({ ...prev, [dropdownConfig?.id as string]: value }))
        }
        value={selectedOption?.value ?? ''}
      >
        <InputLabel>{dropdownConfig?.subtitle ?? 'Mode'}</InputLabel>
        <SelectTrigger data-state-name={state?.stateName} placeholder="Select Mode" />
        <SelectContent onEscapeKeyDown={keepEscapeInDialog}>
          {dropdownConfig?.options.map((option: DropdownOption) => (
            <SelectItem key={option.label} value={option.value}>
              <span className="inline-flex items-center gap-2">
                {option.icon && (
                  <ActionIcon icon={option.icon.icon} variant={option.icon.variant} />
                )}
                {option.label}
              </span>
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    </ConditionalRender>
  );
};
